import { get } from 'lodash'
import React from 'react'

import { apiEndpoints } from 'api'
import {
  commonAttributes,
  tableColumnDefaultOption,
  uploadPresets
} from 'app/config'
import constants, {
  ANNOUNCEMENT_TYPE_SELECT_OPTIONS,
  ANNOUNCEMENT_STATUS_OPTIONS
} from 'app/constants'
import { validateNotEmpty } from 'app/validators'

const fixedWidthStyle = {
  minWidth: '240px',
  whiteSpace: 'pre-wrap',
  marginRight: '10px'
}

export const columns = [
  {
    ...commonAttributes.uuid
  },
  {
    name: 'type',
    label: 'Announcement Type',
    widget: 'select',
    widgetFilter: true,
    valueLabelMappings: ANNOUNCEMENT_TYPE_SELECT_OPTIONS,
    isValueKey: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'type',
      setCellProps: () => ({
        style: fixedWidthStyle
      })
    }
  },
  {
    name: 'subtype',
    label: 'Announcement Sub-type',
    conditionalRender: (data) => get(data, 'type') !== '',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'subtype',
      setCellProps: () => ({
        style: fixedWidthStyle
      })
    }
  },
  {
    ...commonAttributes.subject,
    name: 'subject',
    label: 'subject',
    formDataKey: 'subject',
    multiple: false,
    resultStructure: {
      keyName: 'subject',
      keyValue: 'uuid'
    },
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid, // Extract the actual UUID
          name: item.name,
          subjectObject: item
        }
      }
      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      }

      return data.results.map((item) => {
        return getItemStructure(item)
      })
    },
    apiOptions: {
      method: 'get',
      url: `${apiEndpoints.subject}?limit=600&offset=0`
    },
    options: {
      ...commonAttributes.subject.options,
      filterKey: 'subject_name',
      customBodyRender: (value) => <span>{value?.name ?? 'N/A'}</span>
    }
  },
  {
    name: 'batch_type',
    label: 'Batch Type',
    showOptionalInPosition: true,
    conditionalRender: (data) =>
      get(data, 'type') === 'Playdate' || get(data, 'type') === 'Trial',
    editable: false,
    defaultValue: (data) =>
      get(data, 'type') === 'Playdate' ? 'event' : 'explore',
    options: {
      display: false,
      ...tableColumnDefaultOption.options,
      filterKey: 'batch_type',
      customBodyRender: (value) => value ?? 'N/A'
    }
  },

  {
    name: 'title',
    label: 'announcement title',
    modalEdit: true,
    validate: validateNotEmpty,
    characterLimit: 60,
    conditionalRender: (data) => get(data, 'type') !== '',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'title'
    }
  },
  {
    name: 'status',
    label: 'Status',
    widget: 'select',
    widgetFilter: true,
    valueLabelMappings: ANNOUNCEMENT_STATUS_OPTIONS,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'status',
      setCellProps: () => ({
        style: {
          minWidth: '150px',
          maxWidth: '150px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  },
  {
    name: 'description',
    label: 'description',
    modalEdit: true,
    validate: validateNotEmpty,
    characterLimit: 100,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      setCellProps: () => ({
        style: { ...fixedWidthStyle, minWidth: '340px' }
      }),
      customBodyRender: (value) => value ?? 'N/A'
    }
  },
  {
    ...commonAttributes.image,
    name: 'image',
    label: 'banner image *',
    fileUrlOnly: true,
    validate: validateNotEmpty,
    uploadPreset: uploadPresets[constants.ANNOUNCEMENT]
  },
  {
    name: 'centres',
    label: 'Centre',
    formDataKey: 'centres',
    widget: 'autocomplete',
    widgetFilter: true,
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    conditionalRender: (data) => get(data, 'type') !== '',
    getStructuredValues: (data) => {
      console.log('data in centres get structured values', data)
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: item.name
        }
      }

      // If data is already in the correct format (when editing), return as is
      if (
        data &&
        Array.isArray(data) &&
        data.length > 0 &&
        data[0] &&
        typeof data[0] === 'object' &&
        data[0].uuid &&
        data[0].name
      ) {
        return data
      }

      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
        return [getItemStructure(data)]
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      } else {
        return data.map((item) => {
          return getItemStructure(item)
        })
      }
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'centres',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      url: apiEndpoints.centre
    },
    multiple: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'centre_name',
      customBodyRender: (value) => {
        if (Array.isArray(value)) {
          return value.map((item) => item.name).join(', ')
        }
        return value?.name || 'N/A'
      },
      setCellProps: () => ({
        style: fixedWidthStyle
      })
    }
  },
  {
    name: 'timings.start_date',
    label: 'event start date',
    conditionalRender: (data) => get(data, 'type') === 'General',
    options: {
      display: false,
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => value ?? 'N/A'
    }
  },
  {
    name: 'timings.end_date',
    label: 'event end date',
    conditionalRender: (data) => get(data, 'type') === 'General',
    options: {
      display: false,
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => value ?? 'N/A'
    }
  },
  {
    name: 'timings.start_time',
    label: 'event start time',
    conditionalRender: (data) => get(data, 'type') === 'General',
    options: {
      display: false,
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => value ?? 'N/A'
    }
  },
  {
    name: 'timings.end_time',
    label: 'event end time',
    conditionalRender: (data) => get(data, 'type') === 'General',
    options: {
      display: false,
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => value ?? 'N/A'
    }
  },
  {
    name: 'tags',
    label: 'tags',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      setCellProps: () => ({
        style: fixedWidthStyle
      }),
      customBodyRender: (tags) =>
        tags?.tagsList ? tags?.tagsList?.join(', ') : 'N/A'
    }
  },
  {
    name: 'cta',
    label: 'cta copy',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'cta',
      setCellProps: () => ({
        style: fixedWidthStyle
      }),
      customBodyRender: (cta_copy) => cta_copy ?? 'N/A'
    }
  },
  {
    name: 'cta_link',
    label: 'cta destination',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      display: false,
      setCellProps: () => ({
        style: fixedWidthStyle
      }),
      customBodyRender: (link) => link ?? 'N/A'
    }
  },
  {
    ...commonAttributes.date,
    modalEdit: true,
    widget: 'datepicker',
    name: 'start_date',
    label: 'visible from',
    options: {
      ...commonAttributes.date.options,
      filterKey: 'start_date'
    }
  },
  {
    ...commonAttributes.date,
    modalEdit: true,
    widget: 'datepicker',
    name: 'end_date',
    label: 'visible till',
    options: {
      ...commonAttributes.date.options,
      filterKey: 'end_date'
    }
  },

  {
    ...commonAttributes.date,
    modalEdit: false,
    disabledInForm: true,
    widget: 'datepicker',
    name: 'created',
    label: 'date created',
    options: {
      ...commonAttributes.date.options,
      filterKey: 'created_date'
    }
  },
  {
    name: 'created_by.full_name',
    label: 'operator',
    modalEdit: false,
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'created_by',
      disableFilter: true
    }
  }
]
